import { type ForwardedRef, type InputHTMLAttributes, forwardRef } from 'react';

import { Container } from './Container';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  /**
   * The translated string to use for the aria-label of the icon that clears the input.
   */
  ariaLabelClearInput: string;

  /**
   * The translated string or <T> element to use when there is an error with value entered into the input.
   */
  errorText?: React.ReactNode;

  /**
   * The state to represent if the input should be disabled.
   */
  isDisabled?: boolean;

  /**
   * The translated string or <T> element to use as the input's label.
   */
  labelText: React.ReactNode;

  /**
   * The function used to clear the value entered into the input.
   */
  onClear: () => void;

  /**
   * The translated string or <T> element to use as the input's sublabel (e.g. info about password restrictions).
   */
  supportingText?: React.ReactNode;

  /**
   * The children to render inside the input.
   */
  customIcon?: React.ReactNode;
};

export const Input = forwardRef(
  (
    {
      ariaLabelClearInput,
      className,
      errorText,
      isDisabled,
      labelText,
      name,
      onChange,
      onClear,
      placeholder,
      supportingText,
      value,
      customIcon,
      ...inputProps
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    return (
      <Container
        ariaLabelClearInput={ariaLabelClearInput}
        className={className}
        errorText={errorText}
        isDisabled={isDisabled}
        labelText={labelText}
        name={name}
        onClear={onClear}
        supportingText={supportingText}
        customIcon={customIcon}
      >
        <input
          disabled={isDisabled}
          id={name}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          ref={ref}
          value={value}
          {...inputProps}
        />
      </Container>
    );
  },
);
